import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

const APP_VERSION = '1.2.0';

// Inline SVG icons (no external icon dependency, matching VoltIQ's existing style)
const IconX = (p) => (<svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>);
const IconLogout = (p) => (<svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>);
const IconClock = (p) => (<svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>);
const IconHelp = (p) => (<svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>);
const IconChevron = (p) => (<svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>);
const IconSun = (p) => (<svg {...p} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zM10 15a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM4 11a1 1 0 100-2H3a1 1 0 000 2h1zm13 0a1 1 0 100-2h-1a1 1 0 100 2h1z" clipRule="evenodd" /></svg>);
const IconMoon = (p) => (<svg {...p} fill="currentColor" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" /></svg>);
const IconCheck = (p) => (<svg {...p} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>);

const UserPanel = ({ isOpen, onClose, user, appName = 'Settings', darkMode = false, setDarkMode, onLogout, onOpenChangeLogs }) => {
  const [showGuide, setShowGuide] = React.useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const userRole = (user?.role || 'User').toUpperCase();
  const surface = darkMode ? 'bg-gray-800' : 'bg-white';
  const actionBtn = `w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors text-sm text-left ${
    darkMode ? 'text-gray-200 border-gray-700 hover:bg-gray-700' : 'text-gray-700 border-gray-100 hover:bg-gray-50'
  }`;

  const panelContent = (
    <>
      <div className="fixed inset-0 bg-black/40 z-[9998]" onClick={onClose} role="button" tabIndex={-1} aria-label="Close panel" />
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-sm ${surface} shadow-2xl z-[9999] flex flex-col`}
        style={{ animation: 'userPanelSlideIn 0.25s ease-out', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)' }}
      >
        <style>{`@keyframes userPanelSlideIn { from { transform: translateX(100%);} to { transform: translateX(0);} }`}</style>

        {/* Close */}
        <div className="flex items-center justify-end p-4">
          <button type="button" onClick={onClose} className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`} aria-label="Close panel">
            <IconX className="w-5 h-5" />
          </button>
        </div>

        {/* Header — person identity if logged in, otherwise an app/settings header */}
        {user ? (
          <div className="px-6 pb-6 flex flex-col items-center">
            <div className="w-24 h-24 rounded-full flex items-center justify-center text-white font-bold text-3xl flex-shrink-0" style={{ backgroundColor: '#679632' }}>
              {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
            </div>
            <h2 className={`mt-4 text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user?.name || 'User'}</h2>
            <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{user?.email || ''}</p>
            <span className="mt-2 px-4 py-1 text-xs font-semibold tracking-wider rounded-full" style={{ color: '#679632', backgroundColor: 'rgba(103,150,50,0.08)', border: '1px solid rgba(103,150,50,0.4)' }}>
              {userRole}
            </span>
          </div>
        ) : (
          <div className="px-6 pb-6 flex flex-col items-center">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center bg-gray-50 overflow-hidden flex-shrink-0">
              <img src="/leftpanel.png" alt="" className="w-16 h-16 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
            <h2 className={`mt-4 text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{appName}</h2>
            <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Settings &amp; help</p>
          </div>
        )}

        <div className={`mx-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`} />

        {/* Actions */}
        <div className="flex-1 px-6 py-4 space-y-1 overflow-y-auto">
          {onOpenChangeLogs && (
            <button type="button" onClick={() => { onOpenChangeLogs(); onClose(); }} className={actionBtn}>
              <IconClock className="w-[18px] h-[18px] text-gray-400 flex-shrink-0" />
              <span>Change Logs</span>
              <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-medium ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-500'}`}>v{APP_VERSION}</span>
            </button>
          )}

          {/* App Guide */}
          <button type="button" onClick={() => setShowGuide(!showGuide)} className={actionBtn}>
            <IconHelp className="w-[18px] h-[18px] text-gray-400 flex-shrink-0" />
            <span>App Guide</span>
            <IconChevron className={`ml-auto w-3.5 h-3.5 text-gray-400 transition-transform ${showGuide ? 'rotate-180' : ''}`} />
          </button>
          {showGuide && (
            <div className={`mx-1 p-4 rounded-xl border text-xs space-y-3 max-h-60 overflow-y-auto ${darkMode ? 'bg-gray-700/50 border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-100 text-gray-700'}`}>
              <p className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>AGS VoltIQ</p>
              <p>Intelligent insights into inverter performance and power flow across all your solar sites.</p>
              <div>
                <p className={`font-semibold mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Pages</p>
                <ul className={`space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li><span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Dashboard</span> — Health overview with per-site ratios, hourly performance charts, and the current file summary.</li>
                  <li><span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Upload</span> — Upload an Excel export to analyze inverter health.</li>
                  <li><span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Upload History</span> — Revisit your last 10 analyses without re-processing.</li>
                </ul>
              </div>
              <div>
                <p className={`font-semibold mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Tips</p>
                <ul className={`space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li>Collapse the sidebar with the chevron on its edge for more room.</li>
                  <li>The green sparkle button opens the AI Assistant for quick answers about your data.</li>
                </ul>
              </div>
            </div>
          )}

          {/* Appearance */}
          {setDarkMode && (
            <div className="pt-2">
              <p className={`text-[11px] uppercase tracking-wider font-semibold mb-2 px-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Appearance</p>
              <div className={`rounded-xl border ${darkMode ? 'bg-gray-700/40 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                <button onClick={() => setDarkMode(false)} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${!darkMode ? 'text-[#679632]' : 'text-gray-300 hover:bg-gray-600/40'}`} style={!darkMode ? { backgroundColor: 'rgba(103,150,50,0.1)' } : undefined}>
                  <span className="flex items-center gap-3"><IconSun className="w-5 h-5" /><span className="text-sm font-medium">Light mode</span></span>
                  {!darkMode && <IconCheck className="w-4 h-4" />}
                </button>
                <button onClick={() => setDarkMode(true)} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${darkMode ? 'text-[#86bb46]' : 'text-gray-600 hover:bg-gray-100'}`} style={darkMode ? { backgroundColor: 'rgba(103,150,50,0.18)' } : undefined}>
                  <span className="flex items-center gap-3"><IconMoon className="w-5 h-5" /><span className="text-sm font-medium">Dark mode</span></span>
                  {darkMode && <IconCheck className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Logout */}
        {onLogout && (
          <div className={`p-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <button type="button" onClick={() => { onLogout(); onClose(); }} className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-white font-medium transition-colors bg-red-600 hover:bg-red-700">
              <IconLogout className="w-[18px] h-[18px]" /> Sign out
            </button>
          </div>
        )}
      </div>
    </>
  );

  return createPortal(panelContent, document.body);
};

export default UserPanel;
